/**
 * @file AssessmentQuestionStacked — a single stacked Likert question
 * @version 0.3.0
 */

import React, { useEffect, useRef, useState } from 'react';
import { AssessmentQuestionStackedOption } from '../AssessmentQuestionStackedOption';
import { Checkbox } from '../Checkbox';
import styles from './AssessmentQuestionStacked.module.css';

/*
 * AssessmentQuestionStacked — a single stacked Likert question card.
 *
 * A statement on the left paired with a labeled numbered-circle scale on the
 * right: an opening endpoint label ("Never"), a run of numbered circles
 * (1…count), and a closing endpoint label ("Always"), all on a translucent
 * teal-grey card. One circle is selectable at a time; picking a number
 * answers the question.
 *
 * Four layout tiers, chosen from the card's OWN rendered width (a
 * ResizeObserver, so the card adapts to its container — a full-bleed step, a
 * narrow column, a popup — not the viewport; pin with `forceLayout` for tests):
 *   - `md` (card >=912px): a single row — statement left, scale right. The
 *     side-by-side layout holds until the scale can no longer sit beside the
 *     statement, then the card stacks.
 *   - `tablet` (card >=528px): statement on top, then a full-width scale row
 *     whose numbered options stretch into fill pills with the endpoint
 *     labels inline; the selected option carries its value label on a
 *     reserved line below.
 *   - `compact` (card >=452px): statement on top, endpoint labels still
 *     inline, but the options are fixed circles (capped at 48px, floored at
 *     38px) spread across the row rather than stretched pills.
 *   - `mobile` (card <452px): statement on top, then the 38px circles, then
 *     the endpoint labels spread on their own line below.
 * Because the tier follows the card's width, the statement never keeps a
 * cramped side-by-side column — the card restacks before the text collapses.
 * The two stacked tiers have no hover, so the selected option carries its
 * value label persistently; on mobile that label replaces the endpoint
 * labels while shown.
 *
 * Optional pieces:
 *   - `valueLabels` names each number's meaning; the matching label reveals
 *     under a circle on hover/focus (the card's vertical padding reserves the
 *     room, so the reveal never shifts layout).
 *   - `notObserved` appends a checkbox column behind a hairline divider for
 *     raters who couldn't observe the behavior. While checked, the scale is
 *     disabled — circles and endpoint labels dim, hover and selection are
 *     blocked. Clearing a previously picked value is the consumer's call.
 *   - `error` marks a failed validation: a 2px magenta ring on the card and
 *     aria-invalid on the radio group. The ring is color-only, so always pair
 *     it with a visible message (render an ErrorMessage below the card); the
 *     consumer sets error on failed validation and clears it when a rating
 *     is picked.
 *
 * The scale is a radio group. Arrow keys move the selection between circles
 * (wrapping at the ends), Home/End jump to the first/last, and only the
 * selected circle (or the first, when unanswered) is in the tab order.
 */

export interface AssessmentQuestionStackedProps {
  /** The question text rendered on the left. */
  statement: string;
  /** Number of circle options. Defaults to 7 (a 1–7 scale). */
  count?: number;
  /** Left endpoint label. */
  endpointStart?: string;
  /** Right endpoint label. */
  endpointEnd?: string;
  /**
   * Optional per-value labels, index-aligned to the circles (labels[0] names
   * value 1). A circle with a label reveals it beneath itself on hover/focus.
   */
  valueLabels?: string[];
  /** Currently selected number (1…count). Undefined = unanswered. */
  value?: number;
  /** Fires with the picked number. Omit for a static reference render. */
  onChange?: (value: number) => void;
  /** Shows the Not Observed checkbox column behind a hairline divider. */
  notObserved?: boolean;
  /** Checked state of the Not Observed checkbox. While checked, the scale is disabled. */
  notObservedChecked?: boolean;
  /** Fires with the new checked state. Omit for a static reference render. */
  onNotObservedChange?: (checked: boolean) => void;
  /** Label for the Not Observed column. */
  notObservedLabel?: string;
  /**
   * Validation error state — draws a 2px magenta ring on the card and sets
   * aria-invalid on the radio group. Pair with a visible message below the
   * card (e.g. ErrorMessage); the ring never signals alone.
   */
  error?: boolean;
  /**
   * Pin the layout tier (skips the width-based auto-detect) — for tests and
   * any context that wants a fixed tier regardless of rendered width.
   */
  forceLayout?: 'md' | 'tablet' | 'compact' | 'mobile';
  /** Explicit width override. Without it the card fills its column (100%). */
  width?: number | string;
  /**
   * Colour treatment. `dark` (default) is the translucent teal-grey card with
   * light ink; `light` is a faint teal-tint card with navy ink + navy-ring
   * option circles. Threads through to the option circles. Per-instance variant.
   */
  theme?: 'light' | 'dark';
  /** Forwarded to the root element. */
  className?: string;
}

export function AssessmentQuestionStacked({
  statement,
  count = 7,
  endpointStart = 'Never',
  endpointEnd = 'Always',
  valueLabels,
  value,
  onChange,
  notObserved = false,
  notObservedChecked = false,
  onNotObservedChange,
  notObservedLabel = 'Not observed',
  error = false,
  forceLayout,
  width = '100%',
  theme = 'dark',
  className,
}: AssessmentQuestionStackedProps) {
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  const [layoutTier, setLayoutTier] = useState<
    'md' | 'tablet' | 'compact' | 'mobile'
  >(forceLayout ?? 'md');

  useEffect(() => {
    if (forceLayout) {
      setLayoutTier(forceLayout);
      return;
    }
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // Tier off the card's OWN width, not the viewport — so it adapts to whatever
    // container it lives in (a full-bleed step, a narrow column, a popup), which
    // viewport media queries can't see. Thresholds are the layout's breakpoints
    // expressed as card widths (viewport breakpoints minus the surrounding side
    // padding, which the reference frames measure at 786/527/452/343 for the
    // 834/575/500/375 viewports):
    //   >=912 -> md       (statement beside the scale)
    //   >=528 -> tablet   (statement on top, endpoints inline, fill pills)
    //   >=452 -> compact  (statement on top, endpoints inline, fixed circles)
    //   else  -> mobile   (statement on top, circles then endpoints stacked)
    const tierFor = (w: number): 'md' | 'tablet' | 'compact' | 'mobile' =>
      w >= 912 ? 'md' : w >= 528 ? 'tablet' : w >= 452 ? 'compact' : 'mobile';
    const update = () =>
      setLayoutTier(tierFor(el.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [forceLayout]);

  // Checking Not Observed takes the scale out of play: circles dim and stop
  // responding to hover, click, and keyboard until it's unchecked.
  const scaleDisabled = notObserved && notObservedChecked;

  // Mobile only: the selected circle shows its value label persistently (no
  // hover on touch), and the endpoint labels yield their row to it. On tablet
  // the label rides its pill on a reserved line, so the endpoints stay put.
  const selectedLabelShowing =
    layoutTier === 'mobile' &&
    value !== undefined &&
    valueLabels?.[value - 1] !== undefined;

  const numbers = Array.from({ length: count }, (_, i) => i + 1);
  const selectedIndex = value !== undefined ? value - 1 : -1;
  // Roving tab order: the selected circle is tabbable; if none is picked, the
  // first circle takes the tab stop so the group is reachable.
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const pick = (index: number) => {
    const clamped = ((index % count) + count) % count; // wrap both ends
    onChange?.(clamped + 1);
    optionRefs.current[clamped]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        pick(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        pick(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        pick(0);
        break;
      case 'End':
        e.preventDefault();
        pick(count - 1);
        break;
      default:
        break;
    }
  };

  const rootClass = [
    styles.row,
    theme === 'light' ? styles.light : '',
    notObserved ? styles.hasNotObserved : '',
    scaleDisabled ? styles.scaleDisabled : '',
    selectedLabelShowing ? styles.labelShowing : '',
    error ? styles.error : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={rootRef}
      className={rootClass}
      data-layout={layoutTier}
      style={{ width }}
    >
      <p className={styles.statement}>{statement}</p>

      <div
        className={styles.vote}
        role="radiogroup"
        aria-label={statement}
        aria-invalid={error || undefined}
      >
        <span className={styles.endpoint}>{endpointStart}</span>
        <div className={styles.options}>
          {numbers.map((n, index) => (
            <AssessmentQuestionStackedOption
              key={n}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              number={n}
              theme={theme}
              hoverLabel={valueLabels?.[index]}
              selected={value === n}
              disabled={scaleDisabled}
              tabIndex={index === tabbableIndex ? 0 : -1}
              onClick={onChange ? () => onChange(n) : undefined}
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
          ))}
        </div>
        <span className={styles.endpoint}>{endpointEnd}</span>
      </div>

      {notObserved && (
        <>
          <div className={styles.divider} aria-hidden="true" />
          <label className={styles.notObserved}>
            <Checkbox
              checked={notObservedChecked}
              onChange={(e) => onNotObservedChange?.(e.target.checked)}
            />
            <span className={styles.notObservedLabel}>{notObservedLabel}</span>
          </label>
        </>
      )}
    </div>
  );
}

AssessmentQuestionStacked.displayName = 'AssessmentQuestionStacked';
